import React, { useEffect, useRef } from 'react';
import { FieldValues, UseFormSetValue } from 'react-hook-form';

import { FormatPhoneNumber } from 'components/input/RHFTextInput';
import { SidebarState } from 'contexts/sidebarStateContext';
import useTMSContext from 'hooks/useTMSContext';
import { applyLoadSuggestion } from 'lib/api/applyLoadSuggestion';
import { Load } from 'types/Load';
import { Maybe, Undef } from 'types/UtilityTypes';
import {
  GenericSuggestion,
  SuggestionPipelines,
} from 'types/suggestions/CoreSuggestions';
import {
  ApptConfirmationChanges,
  CarrierChanges,
  LoadChanges,
} from 'types/suggestions/LoadSuggestions';
import { applyCompleteKitSuggestions } from 'utils/completeKitSuggestion';
import { normalizeDateForTMS } from 'utils/parseDatesForTMSForm';
import { handleClickedSuggestionAndGetElements } from 'utils/suggestionScroll';

const LOAD_VIEW_PIPELINES = new Set<string>([
  SuggestionPipelines.CarrierInfo,
  SuggestionPipelines.ApptConfirmation,
]);

const CARRIER_MERGE_KEYS = [
  'firstDriverName',
  'firstDriverPhone',
  'secondDriverName',
  'secondDriverPhone',
  'truckNumber',
  'trailerNumber',
  'dispatchSource',
  'dispatchCity',
  'dispatchState',
  'dispatchedTime',
  'expectedPickupTime',
] as const;

const DIRTY = { shouldDirty: true, shouldTouch: true } as const;

// Top-level Load View form accordion sections targeted by CI / Appt suggestions.
export enum LoadViewSuggestionSection {
  Carrier = 'carrier',
  Pickup = 'pickup',
  Consignee = 'consignee',
  Route = 'route',
  Stops = 'stops',
}

export function isLoadViewFormSuggestion(
  suggestion: Maybe<GenericSuggestion>,
  freightTrackingID?: Maybe<string>
): boolean {
  if (!suggestion || !LOAD_VIEW_PIPELINES.has(suggestion.pipeline)) {
    return false;
  }

  const suggestionLoadId = (suggestion as { freightTrackingID?: string })
    .freightTrackingID;

  return !freightTrackingID || suggestionLoadId === freightTrackingID;
}

// Surgical apply: keeps unrelated form values, marks suggested fields dirty (yellow).
export function applyLoadViewSuggestionFields<T extends FieldValues>(
  setValue: UseFormSetValue<T>,
  suggestion: Maybe<GenericSuggestion>,
  freightTrackingID?: Maybe<string>,
  stopCount = 0,
  tmsName?: Maybe<string>
): void {
  if (!isLoadViewFormSuggestion(suggestion, freightTrackingID)) {
    return;
  }

  const suggested = suggestion!.suggested as LoadChanges;
  const set = setValue as UseFormSetValue<FieldValues>;

  for (const key of CARRIER_MERGE_KEYS) {
    const value = suggested[key];
    if (value == null) {
      continue;
    }

    const path = `carrier.${key}`;
    if (key.endsWith('Phone')) {
      if (!value) {
        continue;
      }
      set(path, FormatPhoneNumber(String(value)), DIRTY);

      continue;
    }

    set(path, value, DIRTY);
  }

  if (suggested.pickupApptTime) {
    const pickupAppt = tmsName
      ? normalizeDateForTMS(new Date(suggested.pickupApptTime), tmsName)
      : new Date(suggested.pickupApptTime);
    set('pickup.apptStartTime', pickupAppt, DIRTY);
    if (stopCount > 0) {
      set('stops.0.apptStartTime', pickupAppt, DIRTY);
    }
  }

  if (suggested.dropoffApptTime) {
    const dropoffAppt = tmsName
      ? normalizeDateForTMS(new Date(suggested.dropoffApptTime), tmsName)
      : new Date(suggested.dropoffApptTime);
    set('consignee.apptStartTime', dropoffAppt, DIRTY);
    if (stopCount > 0) {
      set(`stops.${stopCount - 1}.apptStartTime`, dropoffAppt, DIRTY);
    }
  }
}

export function useApplyLoadViewSuggestion<T extends FieldValues>(
  setValue: UseFormSetValue<T>,
  clickedSuggestion: Maybe<GenericSuggestion>,
  freightTrackingID: Undef<Maybe<string>>,
  enabled = true,
  // Re-apply after form reset (pass memoizedDefaultValues / load identity)
  formResetKey?: unknown,
  // Live route length at apply time; kept in a ref so stop add/remove does not re-apply.
  stopCount = 0
): void {
  const { tmsName } = useTMSContext();
  const stopCountRef = useRef(stopCount);
  stopCountRef.current = stopCount;

  useEffect(() => {
    if (!enabled) {
      return;
    }

    applyLoadViewSuggestionFields(
      setValue,
      clickedSuggestion,
      freightTrackingID,
      stopCountRef.current,
      tmsName
    );
    // stopCount is read via ref so route length changes do not re-apply suggestions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enabled,
    clickedSuggestion?.id,
    freightTrackingID,
    formResetKey,
    setValue,
    tmsName,
  ]);
}

// Accordion section ids used across Load View TMS forms for CI / Appt fields.
export function getLoadViewSuggestionSections(
  suggestion: Maybe<GenericSuggestion>
): LoadViewSuggestionSection[] {
  if (!suggestion) {
    return [];
  }

  if (suggestion.pipeline === SuggestionPipelines.CarrierInfo) {
    return [LoadViewSuggestionSection.Carrier];
  }

  if (suggestion.pipeline === SuggestionPipelines.ApptConfirmation) {
    const suggested = suggestion.suggested as Partial<ApptConfirmationChanges>;
    const sections: LoadViewSuggestionSection[] = [];
    if (suggested.pickupApptTime) {
      sections.push(
        LoadViewSuggestionSection.Pickup,
        LoadViewSuggestionSection.Route,
        LoadViewSuggestionSection.Stops
      );
    }

    if (suggested.dropoffApptTime) {
      sections.push(
        LoadViewSuggestionSection.Consignee,
        LoadViewSuggestionSection.Route,
        LoadViewSuggestionSection.Stops
      );
    }

    return [...new Set(sections)];
  }

  return [];
}

// Collapse unrelated accordion sections and smooth-scroll to applied fields.
export function useFocusLoadViewSuggestion(
  clickedSuggestion: Maybe<GenericSuggestion>,
  freightTrackingID: Undef<Maybe<string>>,
  setActiveTabs: (tabs: string[]) => void,
  enabled = true,
  availableSections?: string[],
  onScrollTargets?: (targets: {
    scrollTo: Maybe<HTMLElement>;
    mustScrollUntil: Maybe<HTMLElement>;
  }) => void
): void {
  useEffect(() => {
    if (
      !enabled ||
      !isLoadViewFormSuggestion(clickedSuggestion, freightTrackingID)
    ) {
      return;
    }

    const targetSections = getLoadViewSuggestionSections(clickedSuggestion);
    const openSections = availableSections?.length
      ? targetSections.filter((section) => availableSections.includes(section))
      : targetSections;

    if (openSections.length > 0) {
      setActiveTabs(openSections);
    }

    let cancelled = false;
    handleClickedSuggestionAndGetElements(clickedSuggestion).then((targets) => {
      if (cancelled) {
        return;
      }

      onScrollTargets?.(targets);
      // Callers that manage ScrollUntil UI handle scroll themselves.
      if (!onScrollTargets) {
        targets.scrollTo?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    });

    return () => {
      cancelled = true;
    };
    // Intentionally keyed on suggestion identity; scroll callbacks/sections are stable enough.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, clickedSuggestion?.id, freightTrackingID]);
}

export async function acceptLoadViewSuggestion(
  clickedSuggestion: Maybe<GenericSuggestion>,
  submittedLoad: Pick<Load, 'carrier' | 'pickup' | 'consignee'> | Load,
  setCurrentState: React.Dispatch<React.SetStateAction<SidebarState>>,
  clearPrevValues?: () => void,
  tmsName?: Maybe<string>
): Promise<void> {
  if (!clickedSuggestion || !isLoadViewFormSuggestion(clickedSuggestion)) {
    return;
  }

  const isCarrier =
    clickedSuggestion.pipeline === SuggestionPipelines.CarrierInfo;
  const suggestedFields = clickedSuggestion.suggested as CarrierChanges &
    Partial<ApptConfirmationChanges>;

  const existingLoadSuggestion = Object.keys(suggestedFields).reduce(
    (acc, key) => {
      if (isCarrier) {
        acc[key] = submittedLoad.carrier?.[key as keyof CarrierChanges];
        return acc;
      }

      if (key === 'pickupApptTime') {
        acc[key] = submittedLoad.pickup?.apptStartTime;
      } else if (key === 'dropoffApptTime') {
        acc[key] = submittedLoad.consignee?.apptStartTime;
      }

      return acc;
    },
    {} as Record<string, unknown>
  );

  await applyLoadSuggestion(clickedSuggestion.id, {
    existingLoadSuggestion,
  });

  setCurrentState((prev) => ({
    ...prev,
    ...applyCompleteKitSuggestions(
      prev,
      clickedSuggestion.id,
      clickedSuggestion.pipeline,
      tmsName
    ),
  }));

  clearPrevValues?.();
}
